import Breadcrumbs from "components/Common/Breadcrumb"
import React, { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { Button, Card, CardBody, Col, Row } from "reactstrap"
import ReactQuill from "react-quill"
import "react-quill/dist/quill.snow.css"
import {
  getPageContentByType,
  updatePageContentByType,
} from "store/actions/faqAction"
import { showAlert } from "store/reducers/alertReducer"
import Spinners from "components/Shared/Spinners"
import { useNavigate } from "react-router-dom"
import { editorFormats, editorModules } from "components/Common/constants"
import AppTypeTabs from "components/PageContent/AppTypeTabs"
import usePageContentAppType from "hooks/usePageContentAppType"
import PublicPageLinks from "pages/Public/components/PublicPageLinks"
import { isRichTextContentEmpty } from "utils/pageContentUtils"
import useTranslatedQuillHeaders from "hooks/useTranslatedQuillHeaders"

const TermsAndConditions = () => {
  const { t } = useTranslation()

  document.title = t("Terms And Conditions ")

  const navigate = useNavigate()
  const dispatch = useDispatch()
  const quillRefAr = useRef(null)

  const { loading, isLoading, pageContent } = useSelector(state => state.Faqs)
  useTranslatedQuillHeaders(!loading)
  const {
    activeAppType,
    availableAppTypes,
    usersPermissions,
    setActiveAppType,
    hasAnyAccess,
  } = usePageContentAppType("terms")

  useEffect(() => {
    if (!hasAnyAccess) {
      navigate("/dashboard")
    }
  }, [hasAnyAccess, navigate])

  const [description, setDescription] = useState("")
  const [descriptionAr, setDescriptionAr] = useState("")

  const fetchContent = () => {
    const payload = {
      type: "TermsAndConditions",
      appType: activeAppType,
    }
    dispatch(getPageContentByType(payload))
  }

  useEffect(() => {
    if (hasAnyAccess) {
      fetchContent()
    }
  }, [activeAppType, hasAnyAccess])

  useEffect(() => {
    const editor = quillRefAr?.current?.getEditor()
    if (!editor) return

    const root = editor.root
    root.setAttribute("dir", "rtl")
    root.classList.add("ql-rtl")
    root.style.textAlign = "right"

    // Prefer Quill's native RTL list marker rules when content is loaded.
    const length = editor.getLength()
    if (length > 1) {
      editor.formatText(
        0,
        length,
        { direction: "rtl", align: "right" },
        "silent"
      )
    }
  }, [pageContent])

  useEffect(() => {
    if (pageContent) {
      const { description: en, descriptionAr: ar } = pageContent
      setDescription(en || "")
      setDescriptionAr(ar || "")
    }
  }, [pageContent])

  const handleSubmit = () => {
    if (isRichTextContentEmpty(description)) {
      dispatch(
        showAlert({
          text: t("English content cannot be empty or only spaces"),
          type: "error",
        })
      )
      return
    }
    if (isRichTextContentEmpty(descriptionAr)) {
      dispatch(
        showAlert({
          text: t("Arabic content cannot be empty or only spaces"),
          type: "error",
        })
      )
      return
    }

    const payload = {
      type: "TermsAndConditions",
      title: "Terms And Conditions",
      titleAr: "الشروط والأحكام",
      description,
      descriptionAr,
      appType: activeAppType,
    }

    dispatch(updatePageContentByType(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
          fetchContent()
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
      })
      .catch(() => {})
  }

  if (loading) return <Spinners />

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs />
        <Card>
          <CardBody className="border-bottom">
            <AppTypeTabs
              activeAppType={activeAppType}
              availableAppTypes={availableAppTypes}
              onChange={setActiveAppType}
            />
            <PublicPageLinks
              pageType="TermsAndConditions"
              appType={activeAppType}
            />
            <Row>
              <Col xl={12}>
                <div className="d-flex align-items-center">
                  <h5 className="mb-0 card-title flex-grow-1">
                    {t("Terms And Conditions")}
                  </h5>
                </div>
              </Col>
              <div className="mt-4"></div>
              <Col lg={6}>
                <h6 className="fw-bold">{t("English")}</h6>
              </Col>
              <Col lg={6}>
                <h6 className="fw-bold">{t("Arabic")}</h6>
              </Col>
              <Col lg={6}>
                <ReactQuill
                  theme="snow"
                  value={description}
                  onChange={setDescription}
                  modules={editorModules}
                  formats={editorFormats}
                  placeholder={t("Write your terms and conditions here...")}
                  readOnly={usersPermissions?.edit ? false : true}
                />
              </Col>
              <Col lg={6}>
                <div className="rtl-quill-wrap" dir="rtl">
                  <ReactQuill
                    theme="snow"
                    ref={quillRefAr}
                    value={descriptionAr}
                    onChange={setDescriptionAr}
                    placeholder={t("اكتب شروطك وأحكامك هنا...")}
                    readOnly={usersPermissions?.edit ? false : true}
                    modules={editorModules}
                    formats={editorFormats}
                  />
                </div>
              </Col>
              <div className="d-flex justify-content-end mt-3">
                {usersPermissions?.edit && (
                  <Button
                    type="submit"
                    color="success"
                    disabled={isLoading}
                    onClick={handleSubmit}
                  >
                    {t("Update")}
                  </Button>
                )}
              </div>
            </Row>
          </CardBody>
        </Card>
      </div>
    </div>
  )
}

export default TermsAndConditions
